import { ErrorCode as EthersErrorCode, EthersError } from 'ethers'
import { AppkitError } from '../appkit'

const isEthersError = (e: unknown): e is EthersError =>
  ETHERS_ERROR_CODES.includes((<EthersError>e)?.code)

/**
 * [docs](https://docs.ethers.org/v6/api/utils/errors/#ErrorCode)
 */
const ETHERS_ERROR_CODES: EthersErrorCode[] = [
  // Generic Errors
  'UNKNOWN_ERROR',
  'NOT_IMPLEMENTED',
  'UNSUPPORTED_OPERATION',
  'NETWORK_ERROR',
  'SERVER_ERROR',
  'TIMEOUT',
  'BAD_DATA',
  'CANCELLED',

  // Operational Errors
  'BUFFER_OVERRUN',
  'NUMERIC_FAULT',

  // Argument Errors
  'INVALID_ARGUMENT',
  'MISSING_ARGUMENT',
  'UNEXPECTED_ARGUMENT',
  'VALUE_MISMATCH',

  // Blockchain Errors
  'CALL_EXCEPTION',
  'INSUFFICIENT_FUNDS',
  'NONCE_EXPIRED',
  'REPLACEMENT_UNDERPRICED',
  'TRANSACTION_REPLACED',
  'UNCONFIGURED_NAME',
  'OFFCHAIN_FAULT',

  // User Interaction
  'ACTION_REJECTED'
] as const

interface ReownSdkError extends EthersError<'UNKNOWN_ERROR'> {
  error: Error & { code: keyof typeof REOWN_SDK_ERROR_CODE }
}

const isReownSdkError = (e: unknown): e is ReownSdkError =>
  !!REOWN_SDK_ERROR_CODE[(<ReownSdkError>e)?.error?.code]

type ReownSdkErrorCode =
  (typeof REOWN_SDK_ERROR_CODE)[keyof typeof REOWN_SDK_ERROR_CODE]

/**
 * [docs](https://github.com/reown-com/reown_flutter/blob/f14c1c2692142fd7b6e273ee8005c1c36d6ce749/packages/reown_core/lib/utils/errors.dart#L40)
 */
const REOWN_SDK_ERROR_CODE = {
  /* ----- INVALID (1xxx) ----- */
  1001: 'INVALID_METHOD',
  1002: 'INVALID_EVENT',
  1003: 'INVALID_UPDATE_REQUEST',
  1004: 'INVALID_EXTEND_REQUEST',
  1005: 'INVALID_SESSION_SETTLE_REQUEST',
  /* ----- UNAUTHORIZED (3xxx) ----- */
  3001: 'UNAUTHORIZED_METHOD',
  3002: 'UNAUTHORIZED_EVENT',
  3003: 'UNAUTHORIZED_UPDATE_REQUEST',
  3004: 'UNAUTHORIZED_EXTEND_REQUEST',
  /* ----- REJECTED (5xxx) ----- */
  4001: 'USER_REJECTED_SIGN',
  /* ----- REJECTED (5xxx) ----- */
  5000: 'USER_REJECTED',
  5001: 'USER_REJECTED_CHAINS',
  5002: 'USER_REJECTED_METHODS',
  5003: 'USER_REJECTED_EVENTS',
  5100: 'UNSUPPORTED_CHAINS',
  5101: 'UNSUPPORTED_METHODS',
  5102: 'UNSUPPORTED_EVENTS',
  5103: 'UNSUPPORTED_ACCOUNTS',
  5104: 'UNSUPPORTED_NAMESPACE_KEY',
  /* ----- REASON (6xxx) ----- */
  6000: 'USER_DISCONNECTED',
  /* ----- FAILURE (7xxx) ----- */
  7000: 'SESSION_SETTLEMENT_FAILED',
  7001: 'NO_SESSION_FOR_TOPIC',
  /* ----- FAILURE (8xxx) ----- */
  8000: 'REQUEST_EXPIRED_SESSION',
  /* ----- PAIRING (10xxx) ----- */
  10001: 'WC_METHOD_UNSUPPORTED',
  /* ----- AUTH VALIDATION (11xxx) ----- */
  11001: 'MALFORMED_RESPONSE_PARAMS',
  11002: 'MALFORMED_REQUEST_PARAMS',
  11003: 'MESSAGE_COMPROMISED',
  11004: 'SIGNATURE_VERIFICATION_FAILED',
  11005: 'REQUEST_EXPIRED_AUTH',
  11006: 'MISSING_ISSUER_AUTH',
  /* ----- AUTH REJECTED (12xxx) ----- */
  12001: 'USER_REJECTED_AUTH',
  12002: 'USER_DISCONNECTED_AUTH'
} as const

export type ReownAppkitErrorCode =
  | `REOWN_SDK_${ReownSdkErrorCode}`
  | `ETHERS_${EthersErrorCode}`

export type ReownAppkitError = AppkitError<ReownAppkitErrorCode>

const factories = [
  {
    is: isReownSdkError,
    make: (e: ReownSdkError): ReownAppkitError =>
      Object.assign(new AppkitError<ReownAppkitErrorCode>(), e, {
        code: `REOWN_SDK_${REOWN_SDK_ERROR_CODE[e.error.code]}`,
        message: e.shortMessage
      })
  },
  {
    is: isEthersError,
    make: (e: EthersError): ReownAppkitError =>
      Object.assign(new AppkitError<ReownAppkitErrorCode>(), e, {
        code: `ETHERS_${e.code}`,
        message: e.shortMessage
      })
  },
  {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    is: (_e: unknown) => true,
    make: (e: unknown): ReownAppkitError =>
      Object.assign(
        new AppkitError<ReownAppkitErrorCode>(),
        !!e && typeof e === 'object' ? e : { error: e },
        {
          code: 'ETHERS_UNKNOWN'
        }
      )
  }
]

export const makeAppkitError = (
  e: ReownSdkError | EthersError | unknown
): ReownAppkitError => {
  const error = factories.find(v => v.is(e))!.make(e as ReownSdkError)
  console.debug({ makeAppkitError: error })
  return error
}
